import { Button, Form, Input, Modal, Row, Select, Space } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLazyGetLegalEntitiesForSystemQuery } from '@app/api/LegalEntities';
import {
  useCreateMigrationYClientsMutation,
  type ModelMigrationYClients,
} from '@app/api/Migration';
import { CloseIconsForModalWithDataTestId } from '@app/components';
import { DATA_MIGRATION, ERROR_REQUIRED_FIELD, SUCCESS } from '@app/constants';
import { useUserPrint, useWindowSize } from '@app/hooks';
import {
  DM_API_TOKEN,
  DM_LEGAL_ENTITY_ID,
  DM_LOGIN,
  DM_PASSWORD,
} from '@app/pages/DataMigration/constants/formFieldIds';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NLegalEntities } from '@app/types/LegalEntities';

import type { DataMigrationModalProps } from '@pages/DataMigration/DataMigration.types';

import type { FC } from 'react';

export const DataMigrationModal: FC<DataMigrationModalProps> = ({
  isModal,
  setModal,
}) => {
  const { activeBranchId } = useUserPrint();
  const { useForm } = Form;
  const [form] = useForm();
  const { showNotification } = useNotification();
  const [legalEntities, setLegalEntities] = useState<
    NLegalEntities.ModelItemEx[]
  >([]);
  const [selectLoader, setSelectLoader] = useState(false);
  const { innerWidth } = useWindowSize();
  const [createMigrationYClients, { isLoading: loader }] =
    useCreateMigrationYClientsMutation();
  const [getLegalEntitiesForSystem] = useLazyGetLegalEntitiesForSystemQuery();

  useEffect(() => {
    const fetchLegalEntitiesForSystem = async () => {
      try {
        setSelectLoader(true);

        const data = await getLegalEntitiesForSystem({
          ActiveBranchId: activeBranchId,
          includeDeleted: false,
        }).unwrap();

        setLegalEntities(data);
      } catch (error) {
        showNotification(error);
      } finally {
        setSelectLoader(false);
      }
    };

    fetchLegalEntitiesForSystem();
  }, [activeBranchId, getLegalEntitiesForSystem, showNotification]);

  const addMigration = useCallback(async () => {
    if (!activeBranchId) return;

    try {
      const data: ModelMigrationYClients = {
        [DM_LEGAL_ENTITY_ID]: form.getFieldValue(DM_LEGAL_ENTITY_ID),
        [DM_API_TOKEN]: form.getFieldValue(DM_API_TOKEN) || null,
        [DM_LOGIN]: form.getFieldValue(DM_LOGIN) || null,
        [DM_PASSWORD]: form.getFieldValue(DM_PASSWORD) || null,
      };

      await createMigrationYClients({
        ActiveBranchId: activeBranchId,
        data,
      }).unwrap();

      showNotification('Миграция добавлена успешно', SUCCESS);
    } catch (error) {
      showNotification(error);
    } finally {
      setModal(false);
      form.resetFields();
    }
  }, [
    form,
    activeBranchId,
    createMigrationYClients,
    showNotification,
    setModal,
  ]);

  const defaultLegalEntity = useMemo(() => {
    if (legalEntities.length > 0) {
      const dataSortedById = [...legalEntities].sort((a, b) => {
        if (a.Id > b.Id) return 1;
        if (a.Id < b.Id) return -1;
        return 0;
      });
      return dataSortedById[0]?.Id;
    }
    return null;
  }, [legalEntities]);

  const handleCancel = useCallback(() => {
    setModal(false);
    form.resetFields();
  }, [setModal, form]);

  return (
    <Modal
      className={`${DATA_MIGRATION}-modal`}
      title={generateSpanWithDataTestId(
        'Создать миграцию данных',
        'create-data-migration-title'
      )}
      data-testid="create-data-migration-popup"
      closeIcon={<CloseIconsForModalWithDataTestId />}
      open={isModal}
      onCancel={handleCancel}
      okButtonProps={{ style: { display: 'none' } }}
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      <Form form={form} layout="vertical" onFinish={addMigration}>
        <Form.Item
          name={DM_LOGIN}
          label={generateSpanWithDataTestId('Логин', 'login-title')}
          tooltip="Логин пользователя с наивысшими правами доступа"
          rules={[{ required: true, message: ERROR_REQUIRED_FIELD }]}
        >
          <Input placeholder="user-name" data-testid="login-value" />
        </Form.Item>
        <Form.Item
          name={DM_PASSWORD}
          label={generateSpanWithDataTestId('Пароль', 'password-title')}
          rules={[{ required: true, message: ERROR_REQUIRED_FIELD }]}
        >
          <Input
            type="password"
            placeholder="xxxxxx"
            data-testid="password-value"
          />
        </Form.Item>
        <Form.Item
          name={DM_API_TOKEN}
          label={generateSpanWithDataTestId('Токен', 'token-title')}
          tooltip="Специальный ключ, дающий право на выгрузку данных"
          rules={[{ required: true, message: ERROR_REQUIRED_FIELD }]}
        >
          <Input
            type="password"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            data-testid="token-value"
          />
        </Form.Item>
        <Form.Item
          name={DM_LEGAL_ENTITY_ID}
          label={generateSpanWithDataTestId(
            'Юридическое лицо',
            'legal-entity-title'
          )}
          tooltip="Для привязки к мигрируемым филиалам"
          rules={[{ required: true, message: ERROR_REQUIRED_FIELD }]}
          initialValue={defaultLegalEntity}
        >
          <Select
            loading={selectLoader}
            options={legalEntities.map(({ Id, Name }) => ({
              value: Id,
              label: Name,
            }))}
            data-testid="active-legal-entity"
            popupRender={(menu) => (
              <div data-testid="legal-entity-dropdown">{menu}</div>
            )}
            optionRender={(option) => (
              <span data-testid="legal-entity-item">{option.label}</span>
            )}
          />
        </Form.Item>

        <Row justify="end">
          <Space
            direction={innerWidth > 414 ? 'horizontal' : 'vertical'}
            style={{ width: innerWidth > 414 ? 'auto' : '100%' }}
          >
            <Form.Item style={{ margin: 0, padding: 0 }}>
              <Button
                block={innerWidth > 414 ? false : true}
                onClick={handleCancel}
                data-testid="cancel"
              >
                Отменить
              </Button>
            </Form.Item>
            <Form.Item style={{ margin: 0, padding: 0 }}>
              <Button
                block={innerWidth > 414 ? false : true}
                loading={loader}
                type="primary"
                htmlType="submit"
                data-testid="choice"
              >
                Добавить
              </Button>
            </Form.Item>
          </Space>
        </Row>
      </Form>
    </Modal>
  );
};
